import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { BlockPackType } from "../../core/engine/packs/blocks/BlockPackFactory";
import { AnnouncerPackType } from "../../core/engine/packs/announcers/AnnouncerPackFactory";
import { CanvasPackType } from "../../core/engine/packs/canvas/CanvasPackFactory";
import "./Settings.css";

interface PlayerSetupProps {
  onClose?: () => void;
  showCloseButton?: boolean;
}

type TabType = "profile" | "packs";

const DEFAULT_BLOCK_PACK: BlockPackType = "default";
const DEFAULT_FRAG_ANNOUNCER_PACK: AnnouncerPackType = "default";
const DEFAULT_CANVAS_PACK: CanvasPackType = "default";

const PlayerSetup: React.FC<PlayerSetupProps> = ({
  onClose,
  showCloseButton = true,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("profile");

  const [playerName, setPlayerName] = useLocalStorage("playerName", "") as [
    string,
    (value: string) => void
  ];
  const [tempPlayerName, setTempPlayerName] = useState<string>(playerName);

  const [fragAnnouncerPack, setFragAnnouncerPack] = useLocalStorage(
    "fragAnnouncerPack",
    DEFAULT_FRAG_ANNOUNCER_PACK
  ) as [AnnouncerPackType, (value: AnnouncerPackType) => void];
  const [tempFragAnnouncerPack, setTempFragAnnouncerPack] =
    useState<AnnouncerPackType>(fragAnnouncerPack);

  const [blockPack, setBlockPack] = useLocalStorage(
    "blockPack",
    DEFAULT_BLOCK_PACK
  ) as [BlockPackType, (value: BlockPackType) => void];
  const [tempBlockPack, setTempBlockPack] = useState<BlockPackType>(blockPack);

  const [canvasPack, setCanvasPack] = useLocalStorage(
    "canvasPack",
    DEFAULT_CANVAS_PACK
  ) as [CanvasPackType, (value: CanvasPackType) => void];
  const [tempCanvasPack, setTempCanvasPack] =
    useState<CanvasPackType>(canvasPack);

  useEffect(() => {
    setTempPlayerName(playerName);
    setTempFragAnnouncerPack(fragAnnouncerPack);
    setTempBlockPack(blockPack);
    setTempCanvasPack(canvasPack);
  }, [playerName, fragAnnouncerPack, blockPack, canvasPack]);

  const handleSave = () => {
    setPlayerName(tempPlayerName);
    setFragAnnouncerPack(tempFragAnnouncerPack);
    setBlockPack(tempBlockPack);
    setCanvasPack(tempCanvasPack);
    // Dispatch event for canvas pack change
    window.dispatchEvent(new Event("canvasPackChanged"));
    if (onClose) {
      onClose();
    } else {
      navigate("/");
    }
  };

  const handleCancel = () => {
    setTempPlayerName(playerName);
    setTempFragAnnouncerPack(fragAnnouncerPack);
    setTempBlockPack(blockPack);
    setTempCanvasPack(canvasPack);
    if (onClose) {
      onClose();
    } else {
      navigate("/");
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-content">
        <div className="settings-header">
          <h1>Player Setup</h1>
          {showCloseButton && (
            <button className="settings-close" onClick={handleCancel}>
              ×
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="tabs-container">
          <button
            className={`tab-button ${activeTab === "profile" ? "active" : ""}`}
            onClick={() => setActiveTab("profile")}
          >
            Profile
          </button>
          <button
            className={`tab-button ${activeTab === "packs" ? "active" : ""}`}
            onClick={() => setActiveTab("packs")}
          >
            Packs
          </button>
        </div>

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <>
            <div className="settings-section">
              <h2>Your Name</h2>
              <p className="settings-hint">
                Set your player name for multiplayer games. Leave empty for a
                random name.
              </p>
              <div className="input-group" style={{ marginBottom: "2rem" }}>
                <label htmlFor="playerName">
                  <span className="label-text">Player Name</span>
                  <span className="label-optional">(optional)</span>
                </label>
                <input
                  type="text"
                  id="playerName"
                  value={tempPlayerName}
                  onChange={(e) => setTempPlayerName(e.target.value)}
                  maxLength={20}
                  placeholder="Enter your name (random name if empty)"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    fontSize: "1rem",
                    border: "1px solid var(--border-color, #444)",
                    borderRadius: "4px",
                    background: "var(--bg-secondary, #1a1a2e)",
                    color: "var(--text-primary, #fff)",
                  }}
                />
                <p
                  className="settings-hint"
                  style={{
                    marginTop: "0.5rem",
                    fontSize: "0.875rem",
                    color: "var(--text-secondary, #aaa)",
                  }}
                >
                  This name will be used when joining or hosting multiplayer
                  games. If left empty, a random name will be generated.
                </p>
              </div>
            </div>
          </>
        )}

        {/* Packs Tab */}
        {activeTab === "packs" && (
          <>
            <div className="settings-section">
              <h2>Block Pack</h2>
              <p className="settings-hint">
                Choose the visual style for game blocks
              </p>
              <div className="block-renderer-config">
                <div className="block-renderer-options">
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="blockPack"
                      value="default"
                      checked={tempBlockPack === "default"}
                      onChange={(e) =>
                        setTempBlockPack(e.target.value as BlockPackType)
                      }
                    />
                    <span>Classic</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="blockPack"
                      value="fancy"
                      checked={tempBlockPack === "fancy"}
                      onChange={(e) =>
                        setTempBlockPack(e.target.value as BlockPackType)
                      }
                    />
                    <span>Fancy</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="blockPack"
                      value="cement"
                      checked={tempBlockPack === "cement"}
                      onChange={(e) =>
                        setTempBlockPack(e.target.value as BlockPackType)
                      }
                    />
                    <span>Cement</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="blockPack"
                      value="glass"
                      checked={tempBlockPack === "glass"}
                      onChange={(e) =>
                        setTempBlockPack(e.target.value as BlockPackType)
                      }
                    />
                    <span>Glass</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h2>Frag Announcer Pack</h2>
              <p className="settings-hint">
                Choose how frag notifications appear when you eliminate an
                opponent.
              </p>
              <div className="block-renderer-config">
                <div className="block-renderer-options">
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="fragAnnouncerPack"
                      value="default"
                      checked={tempFragAnnouncerPack === "default"}
                      onChange={(e) =>
                        setTempFragAnnouncerPack(
                          e.target.value as AnnouncerPackType
                        )
                      }
                    />
                    <span>Text</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="fragAnnouncerPack"
                      value="fireworks"
                      checked={tempFragAnnouncerPack === "fireworks"}
                      onChange={(e) =>
                        setTempFragAnnouncerPack(
                          e.target.value as AnnouncerPackType
                        )
                      }
                    />
                    <span>Fireworks 🎆</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="fragAnnouncerPack"
                      value="kit-kit-miau-miau"
                      checked={tempFragAnnouncerPack === "kit-kit-miau-miau"}
                      onChange={(e) =>
                        setTempFragAnnouncerPack(
                          e.target.value as AnnouncerPackType
                        )
                      }
                    />
                    <span>Kit Kit Miau Miau 🐱</span>
                  </label>
                </div>
              </div>
              <p
                className="settings-hint"
                style={{
                  marginTop: "0.5rem",
                  fontSize: "0.875rem",
                  color: "var(--text-secondary, #aaa)",
                }}
              >
                Text: Simple sliding text message
                <br />
                Fireworks: Animated fireworks effect with message (shown to
                victims)
                <br />
                Kit Kit Miau Miau: Audio-reactive cat glyphs with synth vocals
                (victims only)
              </p>
            </div>

            <div className="settings-section">
              <h2>Canvas Pack</h2>
              <p className="settings-hint">
                Choose the visual style for the game canvas background and frame
              </p>
              <div className="block-renderer-config">
                <div className="block-renderer-options">
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="canvasPack"
                      value="default"
                      checked={tempCanvasPack === "default"}
                      onChange={(e) =>
                        setTempCanvasPack(e.target.value as CanvasPackType)
                      }
                    />
                    <span>Default</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="canvasPack"
                      value="levelBased"
                      checked={tempCanvasPack === "levelBased"}
                      onChange={(e) =>
                        setTempCanvasPack(e.target.value as CanvasPackType)
                      }
                    />
                    <span>Level-Based</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="canvasPack"
                      value="icy"
                      checked={tempCanvasPack === "icy"}
                      onChange={(e) =>
                        setTempCanvasPack(e.target.value as CanvasPackType)
                      }
                    />
                    <span>Icy ❄️</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="canvasPack"
                      value="cosmic"
                      checked={tempCanvasPack === "cosmic"}
                      onChange={(e) =>
                        setTempCanvasPack(e.target.value as CanvasPackType)
                      }
                    />
                    <span>Cosmic 🌌</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="canvasPack"
                      value="forest"
                      checked={tempCanvasPack === "forest"}
                      onChange={(e) =>
                        setTempCanvasPack(e.target.value as CanvasPackType)
                      }
                    />
                    <span>Forest 🌳</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="canvasPack"
                      value="lava"
                      checked={tempCanvasPack === "lava"}
                      onChange={(e) =>
                        setTempCanvasPack(e.target.value as CanvasPackType)
                      }
                    />
                    <span>Volcanic 🔥</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="canvasPack"
                      value="neon"
                      checked={tempCanvasPack === "neon"}
                      onChange={(e) =>
                        setTempCanvasPack(e.target.value as CanvasPackType)
                      }
                    />
                    <span>Neon Grid 💻</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="canvasPack"
                      value="deepSea"
                      checked={tempCanvasPack === "deepSea"}
                      onChange={(e) =>
                        setTempCanvasPack(e.target.value as CanvasPackType)
                      }
                    />
                    <span>Deep Sea 🌊</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="canvasPack"
                      value="retro"
                      checked={tempCanvasPack === "retro"}
                      onChange={(e) =>
                        setTempCanvasPack(e.target.value as CanvasPackType)
                      }
                    />
                    <span>Retro Arcade 💡</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="canvasPack"
                      value="zenGarden"
                      checked={tempCanvasPack === "zenGarden"}
                      onChange={(e) =>
                        setTempCanvasPack(e.target.value as CanvasPackType)
                      }
                    />
                    <span>Zen Garden ⛩️</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="canvasPack"
                      value="alienRuin"
                      checked={tempCanvasPack === "alienRuin"}
                      onChange={(e) =>
                        setTempCanvasPack(e.target.value as CanvasPackType)
                      }
                    />
                    <span>Alien Ruin 👽</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="canvasPack"
                      value="madScience"
                      checked={tempCanvasPack === "madScience"}
                      onChange={(e) =>
                        setTempCanvasPack(e.target.value as CanvasPackType)
                      }
                    />
                    <span>Mad Science 🧪</span>
                  </label>
                  <label className="block-renderer-option">
                    <input
                      type="radio"
                      name="canvasPack"
                      value="pyrotechnic"
                      checked={tempCanvasPack === "pyrotechnic"}
                      onChange={(e) =>
                        setTempCanvasPack(e.target.value as CanvasPackType)
                      }
                    />
                    <span>Pyrotechnic 🔥</span>
                  </label>
                </div>
              </div>
              <p
                className="settings-hint"
                style={{
                  marginTop: "0.5rem",
                  fontSize: "0.875rem",
                  color: "var(--text-secondary, #aaa)",
                }}
              >
                Default: Colorful radial gradient background
                <br />
                Level-Based: Background image changes with game level
                <br />
                Icy: Frozen theme with ice crystals and frost effects
                <br />
                Cosmic: Deep space with stars and nebula
                <br />
                Forest: Nature theme with falling leaves
                <br />
                Volcanic: Fiery lava and smoke effects
                <br />
                Neon Grid: Cyberpunk digital grid
                <br />
                Deep Sea: Underwater theme with bubbles
                <br />
                Retro Arcade: 8-bit style with scanlines
                <br />
                Zen Garden: Minimalist bamboo and sand
                <br />
                Alien Ruin: Eerie bio-luminescent structures
                <br />
                Mad Science: Laboratory with electricity and bubbles
                <br />
                Pyrotechnic: Dynamic fireworks and smoke
                <br />
              </p>
            </div>
          </>
        )}

        <div className="settings-actions">
          <div className="settings-button-group">
            <button
              className="settings-button secondary"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button className="settings-button primary" onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerSetup;
